import { useState, useEffect, useCallback } from 'react';
import { localDataService, QueryOptions } from '../services/localDataService';

export interface UseLocalDataOptions extends QueryOptions {
  entity: string;
  autoSync?: boolean;
}

export interface UseLocalDataReturn {
  data: any[];
  total: number;
  loading: boolean;
  error: string | null;
  syncStatus: {
    pendingItems: number;
    lastSyncTime: number | null;
    isOnline: boolean;
  };
  create: (data: any, syncPayload?: any) => Promise<any>;
  update: (id: string, data: any, syncPayload?: any) => Promise<any>;
  delete: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  sync: () => Promise<any>;
  updateLocalData: (updater: (data: any[]) => any[]) => void;
  query: (entity: string, queryOptions: QueryOptions) => Promise<{ data: any[]; total: number }>;
}

export function useLocalData(options: UseLocalDataOptions): UseLocalDataReturn {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState({
    pendingItems: 0,
    lastSyncTime: null,
    isOnline: navigator.onLine,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await localDataService.query(options.entity, options);
      setData(result.data);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [
    options.entity,
    options.page,
    options.limit,
    options.search,
    options.filters,
    options.sortBy,
    options.sortOrder,
  ]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await localDataService.getSyncStatus();
      setSyncStatus(status);
    } catch (err) {
      console.error('Failed to load sync status:', err);
    }
  }, []);

  const create = useCallback(
    async (newData: any, syncPayload?: any) => {
      try {
        const result = await localDataService.create(options.entity, newData, syncPayload);
        await loadSyncStatus();
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create item');
        throw err;
      }
    },
    [options.entity, loadSyncStatus]
  );

  const update = useCallback(
    async (id: string, updateData: any, syncPayload?: any) => {
      try {
        const result = await localDataService.update(options.entity, id, updateData, syncPayload);
        await loadSyncStatus();
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update item');
        throw err;
      }
    },
    [options.entity, loadSyncStatus]
  );

  const deleteItem = useCallback(
    async (id: string) => {
      try {
        await localDataService.delete(options.entity, id);
        await loadSyncStatus();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete item');
        throw err;
      }
    },
    [options.entity, loadSyncStatus]
  );

  const refresh = useCallback(async () => {
    await loadData();
    await loadSyncStatus();
  }, [loadData, loadSyncStatus]);

  const sync = useCallback(async () => {
    try {
      const result = await localDataService.sync();
      await loadSyncStatus();
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync');
      throw err;
    }
  }, [loadSyncStatus]);

  useEffect(() => {
    loadData();
    loadSyncStatus();
  }, [loadData, loadSyncStatus]);

  useEffect(() => {
    const handleOnline = () => {
      setSyncStatus((prev) => ({ ...prev, isOnline: true }));
      if (options.autoSync) {
        sync();
      }
    };

    const handleOffline = () => {
      setSyncStatus((prev) => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [options.autoSync, sync]);

  return {
    data,
    total,
    loading,
    error,
    syncStatus,
    create,
    update,
    delete: deleteItem,
    refresh,
    sync,
    updateLocalData: (updater: (data: any[]) => any[]) => {
      setData((currentData) => {
        const newData = updater(currentData);
        setTotal(newData.length);
        return newData;
      });
    },
    query: localDataService.query.bind(localDataService),
  };
}

export interface UseLocalDataItemOptions {
  entity: string;
  id: string;
  autoSync?: boolean;
}

export interface UseLocalDataItemReturn {
  data: any | null;
  loading: boolean;
  error: string | null;
  syncStatus: {
    pendingItems: number;
    lastSyncTime: number | null;
    isOnline: boolean;
  };
  update: (data: any) => Promise<any>;
  delete: () => Promise<void>;
  refresh: () => Promise<void>;
  sync: () => Promise<any>;
}

export function useLocalDataItem(options: UseLocalDataItemOptions): UseLocalDataItemReturn {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState({
    pendingItems: 0,
    lastSyncTime: null,
    isOnline: navigator.onLine,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await localDataService.get(options.entity, options.id);
      setData(result?.data || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [options.entity, options.id]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await localDataService.getSyncStatus();
      setSyncStatus(status);
    } catch (err) {
      console.error('Failed to load sync status:', err);
    }
  }, []);

  const update = useCallback(
    async (updateData: any) => {
      try {
        const result = await localDataService.update(options.entity, options.id, updateData);
        setData(result);
        await loadSyncStatus();
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update item');
        throw err;
      }
    },
    [options.entity, options.id, loadSyncStatus]
  );

  const deleteItem = useCallback(async () => {
    try {
      await localDataService.delete(options.entity, options.id);
      setData(null);
      await loadSyncStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
      throw err;
    }
  }, [options.entity, options.id, loadSyncStatus]);

  const refresh = useCallback(async () => {
    await loadData();
    await loadSyncStatus();
  }, [loadData, loadSyncStatus]);

  const sync = useCallback(async () => {
    try {
      const result = await localDataService.sync();
      await loadSyncStatus();
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync');
      throw err;
    }
  }, [loadSyncStatus]);

  useEffect(() => {
    loadData();
    loadSyncStatus();
  }, [loadData, loadSyncStatus]);

  useEffect(() => {
    const handleOnline = () => {
      setSyncStatus((prev) => ({ ...prev, isOnline: true }));
      if (options.autoSync) {
        sync();
      }
    };

    const handleOffline = () => {
      setSyncStatus((prev) => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [options.autoSync, sync]);

  return {
    data,
    loading,
    error,
    syncStatus,
    update,
    delete: deleteItem,
    refresh,
    sync,
  };
}
